export const meta = {
  id: '22-perfect',
  order: 22,
  checkFocus: { tense: ['perfect'] },   // what the end-of-lesson check tests first
  titleFi: 'Perfekti',
  titleEn: 'Perfect',
  duolingoUnit: 'duo-25-perfect',
  lemmaIds: ['emagon', 'sīr', 'pēko', 'ours:qubennon', 'ours:gerpilla', 'ours:henumāzilla', 'ours:ulōrnon', 'ours:ērinilla', 'ours:mīteptir', 'ours:rakellāzma', 'ours:bardunītsos', 'ours:tempusi', 'ours:kīvikagon', 'ours:ibarduty', 'ours:dīnilaksir', 'ours:versioni', 'ours:signatura', 'ours:materiali'],
  exampleIds: [
    'ex-572e11a612e1',
    'ex-d288d6de01a5',
    'ex-2713f106e290',
    'ex-3a12132f449f',
    'hotd-s2e07-005',
    'hotd-s1e04-007',
    'hotd-s1e08-004',
    'hotd-s1e02-013',
    'ex-2605a068d83c',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Perfect',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Perfect',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Imperfect',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Subjunctive',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Negative_statements',
    'https://archiveofourown.org/works/57041695/chapters/146984197',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['perfect'],
  drills: { tense: ['perfect'] },
  showLines: ['hotd-s2e07-005', 'hotd-s1e04-007', 'hotd-s1e08-004', 'hotd-s1e02-013'],
  pronunciation: { focus: 'long-vowels', words: ['sīr', 'blēnon', 'dāria', 'trēsy', 'sȳndrilla'] },
  letter: {
    fi: 'Kerro, mitä olet tehnyt tällä viikolla (perfekti).',
    en: 'Write what you have done this week (perfect).',
  },
  scene: 'hotd-s1e04-1',
}

# Perfekti

Tähän asti olet puhunut nykyhetkestä: preesens kertoo, mitä tapahtuu nyt, ja käskyt osoittavat eteenpäin. Tällä oppitunnilla katsomme ensimmäistä kertaa taaksepäin. Valyrian perfekti kertoo, että jokin on tehty – teko on valmis.

## Valmis teko, ei niinkään aikamuoto

Duolingon Tips & Notes kuvaa perfektiä pikemminkin *aspektiksi* kuin aikamuodoksi. Se ei ensisijaisesti kerro, *milloin* jotain tapahtui, vaan sen, että teko on päättynyt. Käytännössä se käännetään useimmiten menneellä ajalla: <Hv glossed>Ziry ūndan</Hv> on ’näin hänet’. Joskus luontevampi käännös on suomen perfekti: <Hv glossed>Issa, iprattan</Hv> on ’kyllä, olen syönyt’.

Valyrian kannalta tällä erolla ei ole merkitystä: jokaisen perfektin voi kääntää kummalla tahansa tavalla – *näin* tai *olen nähnyt*, *söin* tai *olen syönyt*. Suomesta on tässä paljon apua: sinulla on jo kaksi menneen ajan muotoa, ja käännökseen valitset niistä sen, joka kuulostaa oikealta.

<Compare fi="näi|n" hv="ūnd|an" label="minä-muoto: -n ~ -an" />

<Example id="ex-572e11a612e1" />

<Example id="ex-d288d6de01a5" />

<Note>
Termit menevät tässä helposti sekaisin. Suomen koulukieliopin **imperfekti** (*söin*, *näin*) on tavallinen mennyt aika. Valyrian **imperfekti**, johon tutustumme myöhemmin, on aivan eri asia: se kertoo teosta, joka *oli käynnissä* menneisyydessä (*oli syömässä*). Kun tällä kurssilla puhutaan imperfektistä, se tarkoittaa aina valyrian imperfektiä. Suomen *söin* vastaa valyriassa yleensä perfektiä.
</Note>

## Muodostus: t ja tutut päätteet

Perfekti on yllättävän helppo muodostaa. Useimmiten juureen lisätään **t**, ja sen perään tulevat samat persoonapäätteet, jotka tunnet jo konsonanttiloppuisten verbien preesensistä: <Hv>-an</Hv>, <Hv>-ā</Hv>, <Hv>-as</Hv>, <Hv>-i</Hv>, <Hv>-āt</Hv>, <Hv>-is</Hv>. Esimerkkinä on verbi <Hv glossed>jorrāelagon</Hv> ’rakastaa’:

<Paradigm lemma="jorrāelagon" filter="indicative.active.perfect" numbers="sg,pl" />

<Seam glossed s="jorrāelt" e="an" /> on siis ’rakastin’ tai ’olen rakastanut’, <Seam glossed s="jorrāelt" e="is" /> ’he rakastivat’. Sama kaava pätee moniin konsonanttiloppuisiin ja lähes kaikkiin vokaaliloppuisiin verbeihin.

<Pronounce word="jorrāeltan" />

Paino ei siirry päätteeseen vaan pysyy toisessa tavussa: jor·RĀEL·tan. Suomalainen painottaisi luonnostaan ensimmäistä tavua, joten ole tässä tarkkana.

## Kun t ei mahdu sellaisenaan

Melko monella konsonanttiloppuisella verbillä on **epäsäännöllinen perfektivartalo**. Se on opeteltava verbin mukana, mutta sen voi usein arvata, koska muutokset johtuvat siitä, että juuren viimeinen äänne joutuu suoraan t:n viereen. Verbissä <Hv glossed>rȳbagon</Hv> ’kuulla’ näkyvät tavallisimmat muutokset:

<Paradigm lemma="rȳbagon" filter="indicative.active.perfect" numbers="sg,pl" />

Kaksi asiaa tapahtuu yhtä aikaa. Pitkä <Hv>ȳ</Hv> lyhenee <Hv>y</Hv>:ksi, ja soinnillinen <Hv>b</Hv> muuttuu soinnittomaksi <Hv>p</Hv>:ksi, koska sitä seuraa soinniton <Hv>t</Hv>. Tuloksena on <Seam glossed s="rypt" e="an" /> ’kuulin’. Monissa epäsäännöllisissä verbeissä vokaali lyhenee tai konsonantti muuttuu soinnittomaksi samaan tapaan; joissakin taas perfektin *t* itse muuttuu soinnilliseksi *d*:ksi.

<Pronounce word="ryptan" />

Suomalaiselle ajatus vartalosta, joka muuttuu päätteen edellä, on tuttu: astevaihtelussa sitä tapahtuu jatkuvasti. Valyrian muutokset ovat vain toisenlaisia, ja ne kannattaa painaa mieleen verbi kerrallaan, samaan tapaan kuin opettelet sanan merkityksen.

<Example id="ex-2713f106e290" />

## Neljä erityistä verbiä

Muutama tavallinen verbi kulkee omia polkujaan. Verbi <Hv glossed>urnegon</Hv> ’nähdä’ on hyvin epäsäännöllinen: sen perfektivartalo on <Hv>ūnd-</Hv>, ja persoonapäätteet liitetään siihen **suoraan, ilman t:tä**.

<Paradigm lemma="urnegon" filter="indicative.active.perfect" numbers="sg,pl" />

<Pronounce word="ūndan" />

Samalla tavalla taipuu kolme muuta keskeistä verbiä:

- <Hv glossed>emagon</Hv> ’olla jollakin, omistaa’ → vartalo <Hv>ēd-</Hv>: <Hv glossed>ēdan</Hv> ’minulla oli’
- <Hv glossed>jagon</Hv> ’mennä’ → vartalo <Hv>ist-</Hv>: <Hv glossed>istan</Hv> ’menin’
- <Hv glossed>sagon</Hv> ’olla’ → vartalo <Hv>ist-</Hv>: <Hv glossed>istan</Hv> ’olin’

Huomaa kaksi viimeistä: perfektissä ’mennä’ ja ’olla’ ovat **täsmälleen samannäköisiä**. Lauseen muut sanat ratkaisevat, kumpi on kyseessä. Kun mukana on määränpää datiivissa, joku on mennyt jonnekin; kun mukana on predikatiivi, joku on ollut jotakin.

<Example id="ex-3a12132f449f" />

<Line id="hotd-s2e07-005" />

<Note>
Viimeisessä virkkeessä on *boni* ’nuo’, vaikka käännös sanoo ”nämä”. Peterson on kertonut tarkoittaneensa sanaa <Hv l="bisy">bisi</Hv> (Petersonin Conlang Dialogue -selitykset, 2024).
</Note>

Ensimmäisessä <Hv glossed>istis</Hv> on ’menivät’, sillä <Hv>Ghīhot</Hv> on datiivi, määränpää. Toisessa repliikissä <Hv glossed>istas</Hv> on ’oli’: <Hv glossed>kōrāzma</Hv> ’jumalanpilkka’ on nominatiivissa, ja lause kertoo, mikä jokin oli.

## Perfektin subjunktiivi

Subjunktiivi muodostetaan samalla tavalla kuin indikatiivi, mutta nyt perfektivartaloon liitetään subjunktiivin päätteet <Hv>-on</Hv>, <Hv>-ō</Hv>, <Hv>-os</Hv>, <Hv>-oty</Hv>, <Hv>-ōt</Hv>, <Hv>-osy</Hv>.

<Paradigm lemma="jorrāelagon" filter="subjunctive.active.perfect" numbers="sg,pl" />

Epäsäännöllisillä verbeillä on tässäkin oma vartalonsa: <Hv>rypton</Hv>, <Hv>ryptosy</Hv>; <Hv>ūndon</Hv>, <Hv>ūndosy</Hv>. Kiellon oppitunnilla opit, että kielteinen lause muodostetaan subjunktiivilla ja sanalla <Hv>daor</Hv>. Sama pätee perfektiin: ’ei ollut’ tai ’ei tehnyt’ on perfektin subjunktiivi ja <Hv>daor</Hv>.

<Line id="hotd-s1e04-007" />

Tässä repliikissä ne ovat rinnakkain: myönteinen <Hv l="sagon">istas</Hv> on indikatiivi, kielteinen <Hv fi="et ole täyttänyt">lēdetō daor</Hv> subjunktiivi. Aiemman lohikäärmeenvartijan repliikin toinen lause toimii samoin:

<Interlinear hv="Ziry zaldrīzāeksio istos daor." gloss="hän-NOM lohikäärmeherra-NOM olla-PRF.SBJV.3SG ei" fi="Hän ei ollut lohikäärmeherra." />

## Sīr – nyt, jo

Sana <Hv glossed>sīr</Hv> on sinulle tuttu merkityksessä ’nyt’. Kun verbi on perfektissä, sanan voi usein kääntää osuvammin sanalla **jo**: teko on ehtinyt valmiiksi jo ennen tätä hetkeä. Suomessa *nyt* ja *jo* ovat eri sanoja. Valyriassa sama sana kattaa kummankin, ja perfektin kanssa merkitys kallistuu kohti sanoja *jo* tai *juuri*.

<Line id="hotd-s1e08-004" />

Tässä <Hv glossed>sīr amastas</Hv> on ’se saapui juuri’: perfekti <Hv l="amāzigon" glossed>amastas</Hv> ja <Hv>sīr</Hv> yhdessä. Myös preesensin kanssa virallinen käännös voi olla ’jo’, kun asiayhteys sitä vaatii:

<Line id="hotd-s1e02-013" />

## Kokonainen päivä menneessä ajassa

Lopuksi lause, jossa on kolme perfektiä peräkkäin. Kaksi niistä on säännöllisiä t-perfektejä, ja keskimmäinen on tuttu <Hv l="jagon">istan</Hv>.

<Example id="ex-2605a068d83c" />

<Interlinear hv="Zān maskirt|an, sindil|iot istan, se ann|ī ityrte|tan." gloss="eilen herätä-PRF.1SG tori-DAT mennä-PRF.1SG ja hevonen-ACC ostaa-PRF.1SG" fi="Eilen heräsin, menin torille ja ostin hevosen." />

<Note>
**Muotojen kuuntelukortit.** Tämän oppitunnin jälkeen päivän korteissa voi tulla vastaan myös tuttujen verbien perfektimuotoja kuuntelukortteina. Sano, mitä verbi tarkoittaa ja kuka teki, esimerkiksi ”perfekti, minä”.
</Note>

## Harjoituslauseita

<Practice hv="Vala ābre ūndas." fi="Mies näki naisen. / Mies on nähnyt naisen." />

<Practice hv="Taoba havon sīr iprattas." fi="Poika on jo syönyt leivän." />

<Practice hv="Vala ābre ūndos daor." fi="Mies ei nähnyt naista." />

<Practice hv="Ziry ryptan." fi="Kuulin hänet." />

## Puhutaan: ohjattu keskustelu

Kun olette molemmat läpäisseet tämän oppitunnin, Yhteisen session Puhutaan-osiossa avautuu askel 4. Nyt voitte kysyä toisiltanne, mitä teitte eilen. Kummallakin on oma tehtäväkorttinsa, ja korteissa on eri tietoja. Kysykää ja kertokaa valyriaksi, kunnes tiedätte molemmat saman. Älkää näyttäkö korttejanne toisillenne.

Mallilauseet saa näkyviin napauttamalla, ja jokainen avattu lause lasketaan avuksi. Apufraaseja saa käyttää vapaasti. Kun olette saaneet selville, napauttakaa ”Saimme selville”. Seuraavalla kerralla sama tehtävä tulee uudestaan roolit vaihdettuina.

<SpeakingStep step={4} />

## Sanat

<Vocab />

<Check />
